/**
 * What a visitor typed in the landing page hero (callout.jp, heycallout.com):
 * the prompt, the number and its country, carried over to the call form.
 *
 * The landing sites are on another origin, so they pass it in the URL
 * fragment (#prefill=1&country=US&phone=...&prompt=...), which never reaches
 * servers or analytics. On load the app moves it into localStorage and drops
 * the fragment from the address bar; the call form takes it once the visitor
 * has signed up or in. localStorage rather than sessionStorage because
 * email sign-up goes through a verification link that may open in a new tab.
 */

import { CountryCode } from './market';

export interface Prefill {
  prompt?: string;
  phone?: string;
  country?: CountryCode;
  /** Only set by "Copy to new call" on a past call; the landing pages don't ask. */
  language?: 'ja' | 'en';
}

const FRAGMENT_KEY = 'prefill';
const STORAGE_KEY = 'callout_prefill';
/** Older than this, what was typed is stale and the form starts empty. */
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

/** The landing sites' country codes; Canada is dialed like the US. */
function toCountryCode(value: string | null): CountryCode | undefined {
  switch ((value || '').toUpperCase()) {
    case 'JP':
      return 'JP';
    case 'US':
    case 'CA':
      return 'US';
    default:
      return undefined;
  }
}

/**
 * If the URL fragment carries a prefill, store it and remove the fragment.
 * Call before anything reads window.location.href (attribution, analytics).
 */
export function capturePrefillFromUrl(): void {
  const hash = new URLSearchParams(window.location.hash.slice(1));
  if (hash.get(FRAGMENT_KEY) !== '1') return;

  const prefill: Prefill = {};
  const prompt = hash.get('prompt')?.trim();
  const phone = hash.get('phone')?.trim();
  const country = toCountryCode(hash.get('country'));
  if (prompt) prefill.prompt = prompt;
  if (phone) prefill.phone = phone;
  if (country) prefill.country = country;

  savePrefill(prefill);
  try {
    window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
  } catch {
    // Ignore: the fragment stays in the address bar.
  }
}

/** Store a prefill for the next call form, e.g. "Copy to new call" on a past call. */
export function savePrefill(prefill: Prefill): void {
  try {
    if (prefill.prompt || prefill.phone) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...prefill, savedAt: Date.now() }));
    }
  } catch {
    // Storage disabled: the form just starts empty.
  }
}

/** The stored prefill, or null when there is none or it is stale. */
export function readPrefill(): Prefill | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const { savedAt, prompt, phone, country, language } = JSON.parse(raw);
    if (typeof savedAt !== 'number' || Date.now() - savedAt > MAX_AGE_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    const prefill: Prefill = {};
    if (typeof prompt === 'string' && prompt) prefill.prompt = prompt;
    if (typeof phone === 'string' && phone) prefill.phone = phone;
    const code = toCountryCode(typeof country === 'string' ? country : null);
    if (code) prefill.country = code;
    if (language === 'ja' || language === 'en') prefill.language = language;
    return prefill;
  } catch {
    return null;
  }
}

/** Forget the prefill once the form has used it. */
export function clearPrefill(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore.
  }
}
